import React, { useEffect } from 'react';
import styles from './FeedbackFromEditor.module.scss';
import IconPlus from '@assets/icons/plus_circle.svg?react';
import { useFeedbackForm, defaultFeedbackForm } from './useFeddbackFormEdit';
import FormComponent from './form-component/FormComponent';
import Checkbox from '@components/checkbox/Checkbox';
import { TextInput, TextInputValue } from '@components/text-input/TextInput';
import Select from '@components/select/Select';
import { useQueryDictionary } from '@hooks/useQueryDictionary';
import { SelectOption } from '@types';

type Props = {
  formData: string | undefined;
  showError: boolean;
};

function FeedbackFromEditor(props: Props) {
  const { formData, showError } = props;
  const {
    elements,
    instructions,
    contentInstruction,
    learningOutcomes,
    finalCopy,
    surveyType,
    createQuestion,
    setForm,
    doNotSendToTeacher,
    changeFormValue,
    changeElementsValue
  } = useFeedbackForm();

  const surveyTypesOptions = useQueryDictionary({ dictionary: 'SurveyTypes' });

  useEffect(() => {
    if (formData) {
      setForm({
        ...defaultFeedbackForm,
        ...JSON.parse(formData),
      });
    } else {
      setForm({ ...defaultFeedbackForm });
    }
  }, [formData, setForm]);

  const handleAddNewQuestion = () => {
    createQuestion();
  };

  const changeSendToTeacher = (value: boolean) => {
    changeFormValue(value, 'doNotSendToTeacher');

    elements.forEach((_, index) => {
      changeElementsValue(value, `${index}.doNotSendToTeacher`);
    });
  };

  const handleChangeSurveyInstruction = (value: TextInputValue) => {
    changeFormValue(value, 'instructions');
  };

  const handleChangeContentInstruction = (value: TextInputValue) => {
    changeFormValue(value, 'contentInstruction');
  };

  const handleChangeLearningOutcomes = (value: TextInputValue) => {
    changeFormValue(value, 'learningOutcomes');
  };

  const handleChangeFinalCopy = (value: TextInputValue) => {
    changeFormValue(value, 'finalCopy');
  };

  const handleChangeSurvey = (opt: SelectOption) => {
    changeFormValue(opt.value, 'surveyType')
  }

  const parsedSurveyType = typeof surveyType === 'string' ? parseInt(surveyType) : surveyType

  return (
    <>
      <div className={styles.row}>
        <Select
          label={'Type of survey'}
          options={surveyTypesOptions}
          error={showError && !surveyType}
          subTitle={showError && !surveyType ? 'Type of survey is required.' : ''}
          onChange={handleChangeSurvey}
          value={surveyType}
        />
      </div>

      {parsedSurveyType === 1 && (
        <div className={styles.row}>
          <TextInput
            textArea={true}
            placeholder={'Exit Ticket - Learning Outcomes'}
            label={'Exit Ticket - Learning Outcomes'}
            id={'learning_outcomes'}
            value={learningOutcomes}
            onChange={handleChangeLearningOutcomes}
            allowSpecialCharacters={false}
          />
        </div>
      )}

      <div className={styles.row}>
        <TextInput
          textArea={true}
          placeholder={'Survey Instruction'}
          label={'Survey Instruction'}
          id={'survey_instructions'}
          value={instructions}
          onChange={handleChangeSurveyInstruction}
          allowSpecialCharacters={false}
        />
      </div>

      <div className={styles.row}>
        <TextInput
          textArea={true}
          placeholder={'Content Instruction'}
          label={'Content Instruction'}
          id={'content_instruction'}
          value={contentInstruction}
          onChange={handleChangeContentInstruction}
          allowSpecialCharacters={false}
        />
      </div>

      <div className={styles.row}>
        <TextInput
          textArea={true}
          placeholder={'Final Copy'}
          label={'Final Copy'}
          id={'final_copy'}
          value={finalCopy}
          onChange={handleChangeFinalCopy}
          allowSpecialCharacters={false}
        />
      </div>

      {(!surveyType || parsedSurveyType !== 2) && (
        <div className={styles.row}>
          <Checkbox id={'doNotSendToTeacher'} label={'Do not send survey results to teacher'} checked={doNotSendToTeacher}
                    onChange={changeSendToTeacher}/>
        </div>
      )}

      <div className={styles.content}>
        {elements.map((element, index) => {
          return (
            <FormComponent disableDontSendToTeacher={doNotSendToTeacher || parsedSurveyType === 2} key={index} element={element} index={index}
                           showError={showError}/>
          );
        })}
      </div>
      <div className={styles.addQuestionWrapper}>
        <button type={'button'} className={styles.addQuestionButton} onClick={handleAddNewQuestion}>
          <IconPlus className={styles.plusIcon}/>
          Add question
        </button>
      </div>
    </>
  );
}

const FormEditor = (props: Props) => (
  <FeedbackFromEditor {...props}/>
);

export default FormEditor;
